import { skipToken, useQuery } from '@tanstack/react-query';
import { formatUnits, parseUnits, type Address } from 'viem';
import {
  createClient,
  getTokens,
  getTokenBalances,
  getRoutes,
  executeRoute,
  type SDKClient,
} from '@lifi/sdk';
import type { Token, TokenAmount, Route } from '@lifi/sdk';
import { EthereumProvider } from '@lifi/sdk-provider-ethereum';
import { getWalletClient, switchChain, type Config } from '@wagmi/core';
import { L1 } from '@/modules/chain-constants';
import { useDebouncedValue } from '@/modules/debounce';
import { isSameAddress } from '@/modules/is-same-address';
import { formatPrice, formatUsd } from '@/modules/format';
import { getIsSendTransactionErrorUnexpected } from '@/modules/get-is-send-transaction-error-unexpected';
import { isRateDriftAcceptable } from '@/modules/swap-rate-drift';

// The SAN token contract on Ethereum mainnet.
export const SAN_ADDRESS: Address =
  '0x7c5a0ce9267ed19b22f8cae653f198e3e8daf098';

// LiFi client with wallet access delegated to the given wagmi config.
// Create once at app scope and hand the instance to every hook and
// executeSwap call — nothing here caches it.
export function createSwapClient(config: Config): SDKClient {
  return createClient({
    integrator: 'sanr-app',
    // Without it the SDK pings the npm registry for a version check in dev.
    disableVersionCheck: true,
    providers: [
      EthereumProvider({
        getWalletClient: () => getWalletClient(config),
        switchChain: async (chainId) => {
          const chain = await switchChain(config, { chainId });
          return getWalletClient(config, { chainId: chain.id });
        },
      }),
    ],
  });
}

const CHAIN_ID = L1.id;

const SAN_FALLBACK: Token = {
  address: SAN_ADDRESS,
  chainId: CHAIN_ID,
  symbol: 'SAN',
  decimals: 18,
  name: 'Santiment',
  priceUSD: '0',
};

export type TokenWithBalance = {
  bal: bigint;
  formatted: string;
} & Token;

function withBalance(token: Token, amount = BigInt(0)): TokenWithBalance {
  return {
    ...token,
    bal: amount,
    formatted: formatUnits(amount, token.decimals),
  };
}

export function fmt(value: string, maxDecimals = 4): string {
  return formatPrice(Number.parseFloat(value), { maxDecimals });
}

// Unknown or zero USD value renders as absent (''), never a fabricated $0.
export function fmtUsd(amount: string, priceUSD: string | undefined): string {
  // Stryker disable next-line StringLiteral: equivalent — the fallback only applies when priceUSD is undefined, and both its 0 and the ''-variant's NaN product land in the ''-absent branch below
  const val = Number.parseFloat(amount) * Number.parseFloat(priceUSD ?? '0');
  if (!Number.isFinite(val) || val === 0) return '';
  return formatUsd(val);
}

function usdValue(t: TokenWithBalance): number {
  return Number.parseFloat(t.formatted) * Number.parseFloat(t.priceUSD);
}

export function compareTokensByUsd(
  a: TokenWithBalance,
  b: TokenWithBalance,
): number {
  const aUsd = usdValue(a);
  const bUsd = usdValue(b);
  // An empty or unparsable priceUSD makes a NaN product — no USD value.
  const aHas = aUsd > 0;
  const bHas = bUsd > 0;
  if (aHas !== bHas) return aHas ? -1 : 1;
  if (aHas) return bUsd - aUsd;
  return a.symbol.localeCompare(b.symbol);
}

const EMPTY_TOKENS: TokenWithBalance[] = [];

export function useTokens(
  client: SDKClient,
  address: string | undefined,
): {
  tokens: TokenWithBalance[];
  sanToken: TokenWithBalance;
  loading: boolean;
  refetch: () => Promise<unknown>;
} {
  const addr = address != null && address !== '' ? address : null;
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- `client` is app-lifetime static wiring, not query identity; its functions would hash to an empty object anyway
  const query = useQuery({
    // Stryker disable next-line StringLiteral: equivalent in isolation — the label only namespaces the cache key, and no other query this module issues can collide with the mutated key
    queryKey: ['lifi-tokens', addr],
    // No `enabled` guard: the skipToken branch below already disables the
    // query when there is no address.
    // One attempt (no retries), and no focus/reconnect refetches — consumers
    // cover the form with a loading overlay while `loading` is true, so a
    // background refetch every time the user returns from their wallet tab
    // would flash it (with no defaultOptions on the consumer's QueryClient,
    // v5 defaults of retry 3 + refetchOnWindowFocus would both apply).
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn:
      addr === null ? skipToken : (
        async () => {
          const { tokens: tokenMap } = await getTokens(client, {
            chains: [CHAIN_ID],
          });
          const list = tokenMap[CHAIN_ID] ?? [];
          const balances: TokenAmount[] = await getTokenBalances(
            client,
            addr,
            list,
          );

          const result: TokenWithBalance[] = balances.map((t) =>
            withBalance(t, t.amount ?? BigInt(0)),
          );
          result.sort(compareTokensByUsd);
          return result;
        }
      ),
  });

  // On a failed refetch react-query keeps the last successful data — the
  // old list stays visible.
  const tokens = query.data ?? EMPTY_TOKENS;
  const sanToken =
    tokens.find((t) => isSameAddress(t.address, SAN_ADDRESS)) ??
    withBalance(SAN_FALLBACK);

  // Address-less refetch resolves without touching react-query: rq's
  // refetch() on a skipToken query is a console-logged misuse, not a silent
  // no-op.
  const refetch = async () => (addr === null ? undefined : query.refetch());

  return {
    tokens,
    sanToken,
    // isFetching (not isLoading): a post-swap refetch() must keep reporting
    // `loading`, so consumers hold their overlay up through it.
    loading: query.isFetching,
    refetch,
  };
}

// How often a displayed quote is silently re-fetched — a quote fetched only
// at input time goes stale while the modal idles, and the user would confirm
// a minutes-old receive amount.
const QUOTE_REFRESH_MS = 30_000;

// The wait before a keystroke's quote request fires; inputs must hold still
// this long before getRoutes is called.
const QUOTE_DEBOUNCE_MS = 600;

type QuoteParams = {
  fromAddress: string;
  fromDecimals: number;
  toAddress: string;
  amount: string;
  address: string;
};

export function buildQuoteParams(
  fromAddress: string | undefined,
  fromDecimals: number | undefined,
  toAddress: string | undefined,
  amount: string,
  address: string | undefined,
): QuoteParams | null {
  const parsed = Number.parseFloat(amount);
  if (
    fromAddress == null ||
    fromAddress === '' ||
    fromDecimals == null ||
    toAddress == null ||
    toAddress === '' ||
    address == null ||
    address === '' ||
    Number.isNaN(parsed) ||
    parsed <= 0
  ) {
    return null;
  }
  return { fromAddress, fromDecimals, toAddress, amount, address };
}

export function useQuote(
  client: SDKClient,
  from: TokenWithBalance | null,
  to: TokenWithBalance | null,
  amount: string,
  address: string | undefined,
): { route: Route | null; loading: boolean; error: string } {
  // Token identity (address) + decimals fully determine the quote request;
  // keying on these primitives keeps refetch()-rebuilt token objects from
  // re-firing the quote fetch (deliberate narrowing).
  const fromAddress = from?.address;
  const fromDecimals = from?.decimals;
  const toAddress = to?.address;

  const params = buildQuoteParams(
    fromAddress,
    fromDecimals,
    toAddress,
    amount,
    address,
  );

  // The key carries the RAW inputs, so any change swaps the key and blanks
  // the displayed quote in the same render pass; the fetch itself waits
  // below until the inputs have held still for the debounce window.
  const rawKey = `${fromAddress}|${toAddress}|${amount}|${address}`;
  const settled = useDebouncedValue(rawKey, QUOTE_DEBOUNCE_MS) === rawKey;

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- `client` is app-lifetime static wiring, not query identity; its functions would hash to an empty object anyway
  const query = useQuery({
    // Stryker disable next-line StringLiteral: equivalent in isolation — the label only namespaces the cache key, and no other query this module issues can collide with the mutated key
    queryKey: ['lifi-quote', params],
    enabled: settled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // A re-entered input pair must fetch a fresh quote, never flash a
    // cached one — the receive amount is money the user confirms.
    gcTime: 0,
    // Re-quote the displayed route on an interval so the receive amount the
    // user eventually confirms is at most ~30s old; with nothing on screen
    // (no data) there is nothing to keep fresh.
    refetchInterval: (q) =>
      // Stryker disable next-line BooleanLiteral: equivalent — react-query only schedules numeric intervals, so the mutated `true` is ignored exactly like `false`
      q.state.data !== undefined ? QUOTE_REFRESH_MS : false,
    queryFn:
      params === null ? skipToken : (
        async () => {
          const fromAmount = parseUnits(
            params.amount,
            params.fromDecimals,
          ).toString();
          const { routes } = await getRoutes(client, {
            fromChainId: CHAIN_ID,
            toChainId: CHAIN_ID,
            fromTokenAddress: params.fromAddress,
            toTokenAddress: params.toAddress,
            fromAmount,
            fromAddress: params.address,
          });
          const [bestRoute] = routes;
          // Zero routes throws so a fresh fetch surfaces the error below —
          // while a background re-quote failure keeps the displayed data
          // (react-query keeps data on refetch errors), staying silent.
          if (bestRoute === undefined) throw new Error('No route found');
          return bestRoute;
        }
      ),
  });

  const route = query.data ?? null;
  return {
    route,
    // isLoading, not isFetching: the periodic background re-quote must stay
    // VISUALLY silent (consumers blank the receive amount and disable
    // confirm while `loading`).
    loading: query.isLoading,
    // Errors render only with no route on screen — a transient failure must
    // not paint "No route found" under a kept, still-confirmable route
    // (execution re-prices anyway, gated by the drift guard).
    error:
      route === null && query.error !== null ?
        query.error.message === '' ?
          'Quote failed'
        : query.error.message
      : '',
  };
}

type ExecuteSwapResult =
  // receivedAmount = what execution actually delivered (wei-style integer
  // string), not the quote-time estimate — the two differ on rate updates.
  | { ok: true; receivedAmount: string }
  | { ok: false; rejected: boolean; error: string };

// onStatus receives the active execution action's progress message, plus
// the literal '__signed__' exactly once — when the first tx hash appears.
export async function executeSwap(
  client: SDKClient,
  route: Route,
  onStatus: (msg: string) => void,
): Promise<ExecuteSwapResult> {
  let signedSeen = false;
  try {
    const executed = await executeRoute(client, route, {
      updateRouteHook(r) {
        const acts = r.steps.flatMap((s) => s.execution?.actions ?? []);
        const active = acts.find((a) => a.status !== 'DONE');
        if (active?.message != null && active.message !== '')
          onStatus(active.message);
        if (
          !signedSeen &&
          acts.some((a) => a.txHash != null && a.txHash !== '')
        ) {
          signedSeen = true;
          onStatus('__signed__');
        }
      },
      // Bounded auto-accept — an unconditional `true` here executes any
      // drift silently. Rejecting aborts the step — the swap fails cleanly
      // and the user re-confirms at the refreshed quote.
      acceptExchangeRateUpdateHook: ({ oldToAmount, newToAmount }) =>
        Promise.resolve(isRateDriftAcceptable(oldToAmount, newToAmount)),
    });
    const lastStep = executed.steps[executed.steps.length - 1];
    return {
      ok: true,
      receivedAmount: lastStep?.execution?.toAmount ?? executed.toAmount,
    };
  } catch (err: unknown) {
    const rejected = !getIsSendTransactionErrorUnexpected(err);
    return {
      ok: false,
      rejected,
      error: err instanceof Error ? err.message : 'Swap failed',
    };
  }
}

export type { Token };
